import type { AuthResponse, AuthState } from '~/types';

import { decodeJwtPayload } from './utils';

// =============================================================================
// Storage Keys (namespaced to avoid collisions)
// =============================================================================

const STORAGE_KEYS = {
  accessToken: 'yak_access_token',
  refreshToken: 'yak_refresh_token',
  userId: 'yak_user_id',
  expiresAt: 'yak_expires_at',
  slug: 'yak_slug',
} as const;

// Default TTL: 1 hour in milliseconds (fallback if JWT exp claim unavailable)
const DEFAULT_TTL_MS = 60 * 60 * 1000;

// =============================================================================
// Storage Helpers (graceful degradation if localStorage unavailable)
// =============================================================================

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

// =============================================================================
// Token Management
// =============================================================================

/**
 * Retrieves the current auth state from localStorage.
 */
export function getTokens(): AuthState {
  return {
    accessToken: safeGetItem(STORAGE_KEYS.accessToken),
    refreshToken: safeGetItem(STORAGE_KEYS.refreshToken),
    userId: safeGetItem(STORAGE_KEYS.userId),
    expiresAt: safeGetItem(STORAGE_KEYS.expiresAt)
      ? Number(safeGetItem(STORAGE_KEYS.expiresAt))
      : null,
    slug: safeGetItem(STORAGE_KEYS.slug),
  };
}

/**
 * Stores auth tokens from the API response.
 *
 * Field mapping (API → localStorage):
 * - response.token → yak_access_token (API field is `token`, not `accessToken`)
 * - response.refreshToken → yak_refresh_token
 * - response.user.id → yak_user_id (nested under `user` object)
 * - Derived from JWT → yak_expires_at (decode JWT `exp` claim, or use fixed 1h TTL)
 * - API `redirectUrl` → IGNORED (play app manages its own funnel)
 *
 * Only stores tokens if the auth response indicates success.
 */
export function setTokens(apiResponse: AuthResponse, slug?: string): void {
  // Only store tokens if the response is successful
  if (!apiResponse.success) {
    return;
  }

  safeSetItem(STORAGE_KEYS.accessToken, apiResponse.token);
  safeSetItem(STORAGE_KEYS.refreshToken, apiResponse.refreshToken);
  safeSetItem(STORAGE_KEYS.userId, apiResponse.user.id);

  // Derive expiresAt from JWT exp claim, fallback to fixed TTL
  let expiresAt: number;
  const payload = decodeJwtPayload(apiResponse.token);
  if (payload && typeof payload.exp === 'number') {
    expiresAt = payload.exp * 1000; // JWT exp is in seconds, convert to ms
  } else {
    expiresAt = Date.now() + DEFAULT_TTL_MS;
  }
  safeSetItem(STORAGE_KEYS.expiresAt, expiresAt.toString());

  if (slug) {
    safeSetItem(STORAGE_KEYS.slug, slug);
  }
}

/**
 * Clears all auth tokens from localStorage.
 */
export function clearTokens(): void {
  safeRemoveItem(STORAGE_KEYS.accessToken);
  safeRemoveItem(STORAGE_KEYS.refreshToken);
  safeRemoveItem(STORAGE_KEYS.userId);
  safeRemoveItem(STORAGE_KEYS.expiresAt);
  safeRemoveItem(STORAGE_KEYS.slug);
}

/**
 * Checks if the user currently has a valid (non-expired) auth token.
 */
export function isAuthenticated(): boolean {
  const tokens = getTokens();
  return tokens.accessToken !== null && !isExpired();
}

/**
 * Checks if the stored auth token has expired.
 * Returns true if expired or no expiry set.
 * Note: No token refresh for MVP — expired tokens are cleared, user re-authenticates.
 */
export function isExpired(): boolean {
  const expiresAt = safeGetItem(STORAGE_KEYS.expiresAt);
  if (!expiresAt) return true;
  return Date.now() >= Number(expiresAt);
}

/**
 * Gets the current access token, or null if not available.
 */
export function getAccessToken(): string | null {
  return safeGetItem(STORAGE_KEYS.accessToken);
}
